/**
 * @file     filterChips.ts
 * @readme   ./CLAUDE.md
 * @purpose  Derive "why this match" chips deterministically from an AI filter
 *           state. Server-safe (configs only): used by the chat engine's
 *           context block and re-exported to the chat UI.
 */

import { REGIONS } from '@/core/config/regions';
import { BC_REGIONS, BC_REGION_PREFIX } from '@/core/config/bcAreas';
import { BC_CITIES, CITY_PREFIX } from '@/core/config/bcCities';
import { PILLS_BY_ID } from '@/services/mls/filterPills';
import type { AiFilters } from '@/services/mls/aiFilterSchema';

const REGION_NAME = new Map(REGIONS.map((r) => [r.slug, r.name]));
const BC_NAME = new Map(BC_REGIONS.map((r) => [`${BC_REGION_PREFIX}${r.slug}`, r.label]));
const CITY_NAME = new Map(BC_CITIES.map((c) => [`${CITY_PREFIX}${c.slug}`, c.name]));

const TYPE_LABEL: Record<string, string> = {
  detached: 'Detached',
  townhouse: 'Townhouse',
  condo: 'Condo',
};

function money(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(2).replace(/0$/, '')}M`;
  }
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
}

export function filterChips(filters: AiFilters): string[] {
  const chips: string[] = [];
  for (const slug of filters.regions ?? []) {
    chips.push(REGION_NAME.get(slug) ?? BC_NAME.get(slug) ?? CITY_NAME.get(slug) ?? slug);
  }
  for (const n of filters.neighbourhoods ?? []) chips.push(n.slice(0, 60));
  if (filters.priceMax != null && filters.priceMin != null) chips.push(`${money(filters.priceMin)} to ${money(filters.priceMax)}`);
  else if (filters.priceMax != null) chips.push(`Under ${money(filters.priceMax)}`);
  else if (filters.priceMin != null) chips.push(`Over ${money(filters.priceMin)}`);
  if (filters.rentMax != null && filters.rentMin != null) chips.push(`${money(filters.rentMin)} to ${money(filters.rentMax)}/mo rent`);
  else if (filters.rentMax != null) chips.push(`Under ${money(filters.rentMax)}/mo rent`);
  else if (filters.rentMin != null) chips.push(`Over ${money(filters.rentMin)}/mo rent`);
  if (filters.bedsMin) chips.push(`${filters.bedsMin}+ beds`);
  if (filters.bathsMin) chips.push(`${filters.bathsMin}+ baths`);
  for (const t of filters.propertyTypes ?? []) chips.push(TYPE_LABEL[t] ?? t);
  if (filters.livingAreaMin) chips.push(`${filters.livingAreaMin}+ sqft`);
  if (filters.yearBuiltMin) chips.push(`Built ${filters.yearBuiltMin}+`);
  if (filters.parkingMin) chips.push(`${filters.parkingMin}+ parking`);
  for (const id of filters.pills ?? []) {
    const pill = PILLS_BY_ID[id];
    if (pill) chips.push(pill.label);
  }
  for (const k of filters.keywords ?? []) chips.push(`"${k.slice(0, 40)}"`);
  return chips.slice(0, 10);
}
